import React, { useState } from 'react';
import { RootStateOrAny, useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Button, Grid, Tooltip } from '@material-ui/core';
import {
  setSelectedRangeDate,
  setSelectedStartDate,
} from './../../redux/actions/newOperationHoursActions';
import { snackbarShowMessage } from './../../redux/actions/snackbarActions';
import SpecialEventsReasonsModal from './SpecialEventReasonsModal/SpecialEventsReasonsModal';
import { useFlags } from 'launchdarkly-react-client-sdk';

export default function OperationHoursFooter({
  isNotPLKLockedDayparts = true,
}) {
  const { t } = useTranslation();
  const dispatch: any = useDispatch();

  const {
    selectedStartDate,
    selectedEndDate,
    specialEventArrival,
    dtTimeOpenMode,
    dtTimeCloseMode,
    specialEventDeparture,
    cateringTimeOpenMode,
    cateringTimeCloseMode,
    cateringTimeOpen,
    cateringTimeClose,
    curbsideTimeOpenMode,
    curbsideTimeCloseMode,
    curbsideTimeOpen,
    curbsideTimeClose,
    curbsideIsClosed,
    cateringIsClosed,
    isEditing,
    dtIsClosed,
    dtTimeOpen,
    dtTimeClose,
    digitalDtIsClosed,
    digitalDtTimeOpen,
    digitalDtTimeClose,
    drIsClosed,
    drTimeOpen,
    drTimeClose,
    dtIsOpened24Hours,
    digitalDtIsOpened24Hours,
    drIsOpened24Hours,
    closeToAll,
    isMDSUpAndRunning,
  } = useSelector((state: RootStateOrAny) => state.NewOperationHoursReducer);

  const { ramWarningBarToggle } = useFlags();

  const restaurantServices = useSelector(
    (state: RootStateOrAny) => state.ServicesReducer.entities,
  );

  const brand = useSelector(
    (state: RootStateOrAny) => state.UIReducer.selectedBrand,
  );

  const { RestaurantId, Region, RestaurantStatus } = useSelector(
    (state: RootStateOrAny) => state.RestaurantsReducer.entity,
  );
  const { selectedRestaurantsList: selectedRestaurants } = useSelector(
    (state: RootStateOrAny) => state.RestaurantsReducer,
  );

  const [confirmationModalOpen, setConfirmationModalOpen] =
    useState<boolean>(false);

  const confirmationModalOpenHandler = () => {
    const curbsideService = restaurantServices.find(
      elem => elem.code === 'curbsidePickup',
    );
    const cateringService = restaurantServices.find(
      elem => elem.code === 'catering',
    );
    const driveThruService = restaurantServices.find(
      elem => elem.code === 'driveThru',
    );
    const digitalDriveThruService = restaurantServices.find(
      elem => elem.code === 'digitalDriveThru',
    );
    if (!specialEventArrival && !closeToAll) {
      dispatch(snackbarShowMessage(t('operationHours.arrivalTimeMandatory')));
      return;
    }

    if (
      !specialEventDeparture &&
      !closeToAll &&
      brand === 'TH' &&
      (Region === 'NA' || !RestaurantId)
    ) {
      dispatch(snackbarShowMessage(t('operationHours.DepartureTimeMandatory')));
      return;
    }

    if (
      (!drTimeOpen &&
        (drIsClosed === 'N' || !drIsClosed) &&
        (drIsOpened24Hours === 'N' || !drIsOpened24Hours)) ||
      (!drTimeClose &&
        (drIsClosed === 'N' || !drIsClosed) &&
        (drIsOpened24Hours === 'N' || !drIsOpened24Hours))
    ) {
      dispatch(
        snackbarShowMessage(t('operationHours.diningRoomTimeMandatory')),
      );
      return;
    }
    if (driveThruService && driveThruService.value && RestaurantId) {
      if (
        (!dtTimeOpen &&
          (dtIsClosed === 'N' || !dtIsClosed) &&
          (dtIsOpened24Hours === 'N' || !dtIsOpened24Hours)) ||
        (!dtTimeClose &&
          (dtIsClosed === 'N' || !dtIsClosed) &&
          (dtIsOpened24Hours === 'N' || !dtIsOpened24Hours))
      ) {
        dispatch(
          snackbarShowMessage(t('operationHours.driveThruTimeMandatory')),
        );
        return;
      }
    }

    if (
      digitalDriveThruService &&
      digitalDriveThruService.value &&
      RestaurantId
    ) {
      if (
        (!digitalDtTimeOpen &&
          (digitalDtIsClosed === 'N' || !digitalDtIsClosed) &&
          (digitalDtIsOpened24Hours === 'N' || !digitalDtIsOpened24Hours)) ||
        (!digitalDtTimeClose &&
          (digitalDtIsClosed === 'N' || !digitalDtIsClosed) &&
          (digitalDtIsOpened24Hours === 'N' || !digitalDtIsOpened24Hours))
      ) {
        dispatch(
          snackbarShowMessage(
            t('operationHours.mobileOrderDriveThruTimeMandatory'),
          ),
        );
        return;
      }
    }

    if (
      Array.isArray(cateringTimeOpenMode) &&
      Array.isArray(cateringTimeCloseMode) &&
      cateringTimeOpenMode.includes('write') &&
      cateringTimeCloseMode.includes('write')
    ) {
      if (
        cateringService &&
        cateringService.value &&
        RestaurantId &&
        !(cateringTimeOpen && cateringTimeClose) &&
        ((!closeToAll && brand === 'TH') || brand !== 'TH') &&
        !cateringIsClosed &&
        isNotPLKLockedDayparts
      ) {
        dispatch(snackbarShowMessage(t('operationHours.cateringMandatory')));
        return;
      }
    }

    if (
      Array.isArray(curbsideTimeOpenMode) &&
      Array.isArray(curbsideTimeCloseMode) &&
      curbsideTimeOpenMode.includes('write') &&
      curbsideTimeCloseMode.includes('write')
    ) {
      if (
        curbsideService &&
        curbsideService.value &&
        RestaurantId &&
        (!curbsideTimeOpen || !curbsideTimeClose) &&
        ((!closeToAll && brand === 'TH') || brand !== 'TH') &&
        !curbsideIsClosed
      ) {
        dispatch(snackbarShowMessage(t('operationHours.curbsideMandatory')));
        return;
      }
    }

    setConfirmationModalOpen(!confirmationModalOpen);
  };

  const clearForm = () => {
    if (selectedStartDate && selectedEndDate) {
      dispatch(setSelectedRangeDate(selectedStartDate, selectedEndDate));
    } else if (selectedStartDate) {
      dispatch(setSelectedStartDate(selectedStartDate));
    }
  };

  const isRestaurantClosed = ['Closed', 'Temp Closed'].includes(
    RestaurantStatus,
  );

  function shouldCancelSaveButtonDisabled() {
    if (!isMDSUpAndRunning) return true;
    if (RestaurantId && isRestaurantClosed) return true;
    return false;
  }

  function shouldCancelButtonDisabled() {
    if (shouldCancelSaveButtonDisabled()) return true;
    return !isEditing;
  }

  function shouldSaveButtonDisabled() {
    if (shouldCancelSaveButtonDisabled()) return true;
    return !(
      isEditing &&
      ((selectedRestaurants.length && !RestaurantId) || RestaurantId)
    );
  }

  return (
    <Grid container direction="row" justify="flex-end" style={{ marginTop: 8 }}>
      <Grid item xs="auto" sm="auto" md="auto" lg="auto" xl="auto">
        <Button
          disabled={shouldCancelButtonDisabled()}
          variant="contained"
          color="primary"
          style={{ marginRight: 10 }}
          onClick={clearForm}
        >
          {t('app.common.cancel')}
        </Button>
      </Grid>
      <Grid item xs="auto" sm="auto" md="auto" lg="auto" xl="auto">
        <Button
          disabled={shouldSaveButtonDisabled()}
          variant="contained"
          color="primary"
          onClick={confirmationModalOpenHandler}
        >
          {t('app.common.save')}
        </Button>
      </Grid>
      <SpecialEventsReasonsModal
        confirmationModalOpenHandler={confirmationModalOpenHandler}
        confirmationModalOpen={confirmationModalOpen}
        isNotPLKLockedDayparts={isNotPLKLockedDayparts}
      />
    </Grid>
  );
}
